import { Badge, Button } from '@cloudflare/kumo';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlan, PlanFeature } from '../../contexts/PlanContext';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { FEATURES } from '../../lib/planCatalog';
import { cheapestWith, usePlanCatalog } from './usePlanCatalog';
import { ICONS, Glyph, type IconComponent } from '../ui/icons';
import './featureGate.css';

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const featureIcon = (name: string): IconComponent => (ICONS as Record<string, IconComponent>)[name] ?? ICONS.Lock;

/**
 * Trava de tela por recurso do plano: sem o recurso, a página NÃO é montada
 * (nada de "Agora não" que deixa a tela aberta); no lugar, a vitrine do recurso
 * com o plano que o inclui. O backend continua bloqueando a API (402).
 */
export function FeatureGate({ feature, children }: { feature: PlanFeature; children: ReactNode }) {
  const { has, summary, ready } = usePlan();
  const { user } = useAuth();
  // superadmin vê tudo (o backend também o libera)
  if (user?.role === 'SUPERADMIN') return <>{children}</>;
  // espera o plano carregar: montar a página antes dispararia a API (402) à toa
  if (!ready) return <div className="page-content" aria-busy="true" />;
  if (has(feature)) return <>{children}</>;
  return <LockedFeature feature={feature} currentPlan={summary?.plan?.displayName ?? null} />;
}

function LockedFeature({ feature, currentPlan }: { feature: PlanFeature; currentPlan: string | null }) {
  const info = FEATURES[feature];
  const plans = usePlanCatalog();
  const target = cheapestWith(plans, feature);
  const { can } = useTenant();
  const navigate = useNavigate();
  const canPay = can('billing:manage');

  return (
    <div className="page-content">
      <section className="feature-locked" aria-labelledby="feature-locked-title">
        <div className="feature-locked__icon" aria-hidden><Glyph icon={featureIcon(info.glyph)} size={32}/></div>
        <Badge variant="neutral"><ICONS.Lock size={16} /> Não incluído no {currentPlan ? `plano ${currentPlan}` : 'seu plano'}</Badge>
        <h2 className="text-xl font-semibold feature-locked__title" id="feature-locked-title">{info.title}</h2>
        <p className="feature-locked__pitch">{info.pitch}</p>
        <ul className="feature-locked__bullets">
          {info.bullets.map((b) => <li key={b}><ICONS.Checkmark size={16} /> {b}</li>)}
        </ul>
        {target && (
          <p className="feature-locked__plan">
            Disponível a partir do plano <strong>{target.displayName}</strong> ({brl(target.monthlyPrice)}/mês)
            {target.aiDailyRequests && feature.startsWith('ai') ? `, com ${target.aiDailyRequests.toLocaleString('pt-BR')} pedidos de IA por dia` : ''}.
          </p>
        )}
        <div className="feature-locked__actions">
          {canPay ? (
            <Button variant="primary" onClick={() => navigate(`/faturamento?recurso=${feature}`)}>
              {target ? `Conhecer o plano ${target.displayName}` : 'Ver planos'}
            </Button>
          ) : (
            <p className="feature-locked__ask">Peça ao responsável pelo faturamento da organização para mudar de plano.</p>
          )}
          <Button variant="secondary" onClick={() => navigate('/dashboard')}>Voltar ao início</Button>
        </div>
      </section>
    </div>
  );
}
